import { useAtomValue } from 'jotai';
import AsyncButton from '@wojtekmaj/react-async-button';
import { useTick } from '@wojtekmaj/react-hooks';
import T, { useTranslation } from '@wojtekmaj/react-t';

import { toast } from '@rewardopl/react-toast';

import Button from '@rewardopl/react-ui/button';

import { post } from '@rewardopl/utils/network';

import { vendorCardState } from '../../../../store';

import type { NotRedeemedRewardTransaction } from '@rewardopl/types';
import type { SetStateAction } from 'jotai';

const pendingConfig = {
  disabled: true,
};

const MILLISECONDS_PER_MINUTE = 60_000;

type ReturnButtonProps = {
  setRedeemableTransactions: (
    nextValueOrSetter: SetStateAction<NotRedeemedRewardTransaction[]>,
  ) => void;
  transaction: NotRedeemedRewardTransaction;
};

type CountToReturnDelayEndProps = {
  remainingTime: number;
};

function CountToReturnDelayEnd({ remainingTime }: CountToReturnDelayEndProps) {
  const seconds = Math.floor(remainingTime / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  const mmss = `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;

  return <T mmss={mmss}>{'Return in {mmss}'}</T>;
}

export default function ReturnButton({
  setRedeemableTransactions,
  transaction,
}: ReturnButtonProps) {
  const returnErrorString = useTranslation('Failed to return the reward');

  const { card_subscription_id: cardSubscriptionId, created_at: createdAt } = transaction;

  const card = useAtomValue(vendorCardState);

  const { reward_return_delay: rewardReturnDelay } = card;

  useTick();

  const returnDelayEnd = rewardReturnDelay
    ? new Date(createdAt).getTime() + rewardReturnDelay * MILLISECONDS_PER_MINUTE
    : null;
  const remainingTime = returnDelayEnd ? Math.max(returnDelayEnd - Date.now(), 0) : 0;
  const isReturnDelayed = Boolean(rewardReturnDelay && remainingTime);

  const action = `/api/card_subscriptions/${cardSubscriptionId}/transactions/${transaction._id}/return`;

  async function onClick(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();

    try {
      await post(action);

      setRedeemableTransactions((transactions) =>
        transactions.filter((t) => t._id !== transaction._id),
      );
    } catch {
      toast.error(returnErrorString);
    }
  }

  return (
    <AsyncButton
      as={Button}
      disabled={isReturnDelayed}
      onClick={onClick}
      pendingConfig={pendingConfig}
      type="button"
    >
      {isReturnDelayed && rewardReturnDelay ? (
        <CountToReturnDelayEnd remainingTime={remainingTime} />
      ) : (
        <T>Return</T>
      )}
    </AsyncButton>
  );
}
